import * as queryString from 'query-string';
import { ISearchFieldFiltersOption } from 'interfaces/ISearch';
import { IdName } from 'interfaces/IdName';
import { GeoSpatialSearch } from 'interfaces/ITradeAreaSearch';
import { CustomSearchCriteriaType } from 'components/FindComps/EditSearchCriteria/types';
import { AREA_SEARCH_DATA_REGEX_, AREA_SEARCH_REGEX } from 'constants/regex';
import { convertToNumber } from 'utils/parsers/convertToNumber';

import { CUSTOM_CRITERIA_PREFIX } from './FindCompsSearchCriteriaService';

/**
 * nestVariablesByKeyGroup
 * --------------------------------------------------------------------------
 * @description nests variables whose keys belong to the same group with "or"
 */
const nestVariablesByKeyGroup = (
  variables: ISearchFieldFiltersOption[],
  keyGroup: Set<string>,
): ISearchFieldFiltersOption[] => {
  const nestedVariables: ISearchFieldFiltersOption[] = [];
  let currentNested: ISearchFieldFiltersOption | null = null;

  for (const variable of variables) {
    if (!variable.key || !keyGroup.has(variable.key)) {
      nestedVariables.push(variable);
      continue;
    }

    if (!currentNested) {
      currentNested = { ...variable };
      continue;
    }

    let lastNested = currentNested;
    while (lastNested.or) {
      lastNested = lastNested.or[0];
    }
    lastNested.or = [{ ...variable }];
  }

  if (currentNested) {
    nestedVariables.push(currentNested);
  }

  return nestedVariables;
};

/**
 * nestDockConfigurationTypes
 * --------------------------------------------------------------------------
 * @description  - makes dock configuration keys nested to each other through "or" operator
 */
const nestDockConfigurationTypes = (
  variables: ISearchFieldFiltersOption[],
): ISearchFieldFiltersOption[] =>
  nestVariablesByKeyGroup(
    variables,
    new Set([
      'industrialDockConfigurationType',
      'lifeScienceDockConfigurationType',
      'iosDockConfigurationType',
    ]),
  );

const createNestedOr = (
  items: ISearchFieldFiltersOption[],
): ISearchFieldFiltersOption => {
  if (items.length === 1) return items[0];
  const [first, ...rest] = items;

  return {
    ...first,
    or: [createNestedOr(rest)],
  };
};

/**
 * buildGroupedNestedOrConditions
 * --------------------------------------------------------------------------
 * @description - make nested fields with operator OR into object
 * insert name of key which you want to be nested into keysToNest list
 */
export const buildGroupedNestedOrConditions = (
  variables: ISearchFieldFiltersOption[],
): ISearchFieldFiltersOption[] => {
  const groupedByKey = variables.reduce((acc, variable) => {
    if (!variable.key) return acc;
    if (!acc[variable.key]) {
      acc[variable.key] = [];
    }
    acc[variable.key].push(variable);
    return acc;
  }, {} as Record<string, ISearchFieldFiltersOption[]>);

  const keysToNest = ['railServedIndustrial', 'railProviderIndustrial'];

  return Object.keys(groupedByKey).flatMap(key => {
    if (keysToNest.includes(key)) {
      return [createNestedOr(groupedByKey[key])];
    }
    return groupedByKey[key];
  });
};

/**
 * getVariablesWithNestedFields
 * --------------------------------------------------------------------------
 * @description  - applied nestDockConfigurationTypes and comparing keys
 * ex: typicalClearHeightMaxIndustrial with typicalClearHeightMaxLifeScience/Ios,
 * if they both exist - nest them through "or" so industrial/flex hits the
 * industrial table, life science the life science table, and IOS the IOS table
 */
export const getVariablesWithNestedFields = (
  variables: ISearchFieldFiltersOption[],
): ISearchFieldFiltersOption[] => {
  const industrialAttributeKeyGroups = [
    new Set([
      'typicalClearHeightMaxIndustrial',
      'typicalClearHeightMaxLifeScience',
      'typicalClearHeightMaxIos',
    ]),
    new Set([
      'loadingDocksIndustrial',
      'loadingDocksLifeScience',
      'loadingDocksIos',
    ]),
    new Set([
      'driveInDoorsIndustrial',
      'driveInDoorsLifeScience',
      'driveInDoorsIos',
    ]),
    new Set(['trailerParkingSpaces', 'trailerParkingSpacesIos']),
    new Set(['outsideStorage', 'outsideStorageIos']),
  ];

  let nestedVariables = nestDockConfigurationTypes(variables);
  industrialAttributeKeyGroups.forEach(keyGroup => {
    nestedVariables = nestVariablesByKeyGroup(nestedVariables, keyGroup);
  });

  return nestedVariables;
};

export const buildFilterFieldsPayload = (
  variables: ISearchFieldFiltersOption[],
) => {
  const variablesWithNestedOrPair = getVariablesWithNestedFields(variables);
  const variablesWithNestedOr = buildGroupedNestedOrConditions(
    variablesWithNestedOrPair,
  );

  return {
    and: variablesWithNestedOr,
  };
};

/**
 * Parses areaSearch from URL string based on type
 * @param urlString - the URL string containing areaSearch parameters
 * @param areaSearchType - the type of area search
 * @returns parsed areaSearch object or null if no data found
 */
const parseAreaSearchFromURL = (urlString: string, areaSearchType: string) => {
  switch (areaSearchType) {
    case 'geospatialSearch':
      return parseGeospatialSearchFromURL(urlString, areaSearchType);
    case 'distance':
    case 'travelTime':
      return parseObjectAreaSearchFromURL(urlString, areaSearchType);
    default:
      return null;
  }
};

/**
 * Parses geospatial search data from URL string
 * @param urlString - the URL string containing areaSearch parameters
 * @param areaSearchType - the type of area search (e.g., 'geospatialSearch')
 * @returns parsed areaSearch object or null if no data found
 */
const parseGeospatialSearchFromURL = (
  urlString: string,
  areaSearchType: string,
): { areaSearchType: string; data: GeoSpatialSearch[] } | null => {
  const areaSearchData: GeoSpatialSearch[] = [];

  let match;

  while ((match = AREA_SEARCH_REGEX.exec(urlString)) !== null) {
    const fieldsString = match[2];
    const areaSearchItem = parseAreaSearchItem(fieldsString);

    if (Object.keys(areaSearchItem).length > 0) {
      areaSearchData.push(areaSearchItem);
    }
  }

  return areaSearchData.length
    ? { areaSearchType, data: areaSearchData }
    : null;
};

/**
 * Converts field value to appropriate type based on field name
 * @param fieldName - the name of the field
 * @param fieldValue - the string value from URL
 * @returns converted value (number, boolean, or string)
 */
const convertToAppropriateType = (fieldName: string, fieldValue: string) => {
  const numericFields = [
    'latitude',
    'longitude',
    'radius',
    'minutes',
    'distance',
    'lat',
    'lng',
    'travel_time',
  ];

  if (numericFields.includes(fieldName)) {
    return convertToNumber(fieldValue)!;
  }

  return fieldValue;
};

/**
 * Parses object-based area search data from URL string
 * @param urlString - the URL string containing areaSearch parameters
 * @param areaSearchType - the type of area search (e.g., 'distance', 'travelTime')
 * @returns parsed areaSearch object or null if no data found
 */
const parseObjectAreaSearchFromURL = (
  urlString: string,
  areaSearchType: string,
) => {
  const areaSearchData: Record<string, string | number | IdName[]> = {};

  let match;
  // Regex to find all areaSearchData.{field} fields
  while ((match = AREA_SEARCH_DATA_REGEX_.exec(urlString)) !== null) {
    const fieldName = match[1];
    const fieldValue = match[2];

    if (fieldName && fieldValue) {
      areaSearchData[fieldName] = fieldValue.includes(',')
        ? fieldValue.split(',').map((item: string) => ({ id: item }))
        : convertToAppropriateType(fieldName, fieldValue);
    }
  }

  return Object.keys(areaSearchData).length
    ? { areaSearchType, data: areaSearchData }
    : null;
};

/**
 * Parses a single area search item from fields string
 * @param fieldsString - string like "countryCode:US,geohierarchyLevel:adm1,geoIds:48,geoNames:Texas"
 * @returns parsed area search item object
 */
const parseAreaSearchItem = (fieldsString: string): GeoSpatialSearch => {
  const areaSearchItem: Partial<GeoSpatialSearch> = {};
  const fields = fieldsString.split(',');

  fields.forEach(field => {
    const [key, value] = field.split(':');

    if (!key || !value) return;

    switch (key) {
      case 'geoIds':
        areaSearchItem.geoIds = [{ id: value }];
        break;
      case 'geoNames':
        areaSearchItem.geoNames = [{ id: value }];
        break;
      case 'countryCode':
        areaSearchItem.countryCode = value;
        break;
      case 'geohierarchyLevel':
        areaSearchItem.geohierarchyLevel = value;
        break;
      default:
        break;
    }
  });

  return {
    countryCode: areaSearchItem.countryCode || '',
    geohierarchyLevel: areaSearchItem.geohierarchyLevel || '',
    geoIds: areaSearchItem.geoIds || [],
    geoNames: areaSearchItem.geoNames || [],
  };
};

/**
 * Cleans up areaSearch related fields from the result object
 * @param res - the result object to clean up
 */
const cleanupAreaSearchFields = (res: CustomSearchCriteriaType): void => {
  delete res.areaSearchType;

  Object.keys(res).forEach(key => {
    if (key.startsWith('areaSearch') && key !== 'areaSearch') {
      delete res[key];
    }
  });
};

/**
 * Parses a URL string with filter parameters into an object.
 *
 * Performs a double parsing:
 *  - `raw` is parsed without number/boolean conversion to preserve fields
 *    that must remain unchanged.
 *  - `res` is parsed with number and boolean conversion
 *    (`parseNumbers: true`, `parseBooleans: true`).
 *
 * After parsing, keys listed in `UNCHANGED_FIELDS` are replaced in `res`
 * with their values from `raw`, ensuring these fields are returned exactly
 * as they appear in the query string.
 *
 * @param url - the input URL containing filter parameters
 * @returns an object with filter parameters where keys from `UNCHANGED_FIELDS`
 *          are preserved "as-is", and other values are converted to
 *          numbers/booleans when applicable
 *
 * @example
 * parseInitialUrlStringIntoFilterObject(
 *   'customCriteria[types=1&geoIds=06&countryCode=US]'
 * )
 *
 *  returns something like: geoIds: [{id: "06"] instead of: geoIds: [{id: 6}]
 */
export const parseInitialUrlStringIntoFilterObject = (url: string) => {
  const UNCHANGED_FIELDS = ['geoIds', 'areaSearchData'];

  const str = url.replace(`${CUSTOM_CRITERIA_PREFIX}[`, '').replace(']', '');

  const raw = queryString.parse(str, {
    arrayFormat: 'comma',
    parseNumbers: false,
    parseBooleans: false,
    decode: false,
  });

  const res: any = queryString.parse(str, {
    arrayFormat: 'comma',
    parseNumbers: true,
    parseBooleans: true,
    decode: false,
  });

  if (res.areaSearchType) {
    const areaSearch = parseAreaSearchFromURL(str, res.areaSearchType);

    if (areaSearch) {
      res.areaSearch = areaSearch;
      cleanupAreaSearchFields(res);
    }
  }

  UNCHANGED_FIELDS.forEach(field => {
    if (raw[field]) res[field] = raw[field];
  });

  return res;
};
